import React, { useState, useEffect } from "react"
import axios from "axios"
import configure from "configure"

// ── Timing key — must match StepTiming ───────────────────────────────────────
const timingKey = (serviceId, fi, si, role) => `${serviceId}|${fi}|${si}|${role}`
const getHrs = (timing, k) => timing[k] || 0

const ROLES = ["dev", "ba", "qa"]

const featureRoleTotal = (timing, serviceId, fi, subCount, role) =>
  Array.from({ length: subCount }, (_, si) =>
    getHrs(timing, timingKey(serviceId, fi, si, role))
  ).reduce((a, b) => a + b, 0)

const featureTotal = (timing, serviceId, fi, subCount) =>
  ROLES.reduce(
    (acc, role) => acc + featureRoleTotal(timing, serviceId, fi, subCount, role),
    0
  )

const grandRoleHours = (timing, features, role) =>
  (features || []).reduce(
    (acc, svc) =>
      acc +
      svc.subtitles.reduce(
        (a2, sub, fi) =>
          a2 + featureRoleTotal(timing, svc.serviceId, fi, sub.features.length, role),
        0
      ),
    0
  )

const fmt = (n, symbol = "£") =>
  `${symbol} ${Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

// ─────────────────────────────────────────────────────────────────────────────

const StepOverview = ({
  services,
  features,
  timing,
  pricingMode,
  savedCosting,
  stepLogs,
  allUsers,
}) => {
  // ── Hours (timing is always live from features/timing props) ──────────────
  const devHours = grandRoleHours(timing, features, "dev")
  const baHours = grandRoleHours(timing, features, "ba")
  const qaHours = grandRoleHours(timing, features, "qa")
  const grandHrs = devHours + baHours + qaHours


  // ── Currency symbol from savedCosting ─────────────────────────────────────
  const currSymbol = savedCosting?.currency_symbol || "£"

  // ── Feature counts ────────────────────────────────────────────────────────
  const totalFeatures = features.reduce((a, s) => a + s.subtitles.length, 0)
  const totalSubFeatures = features.reduce(
    (a, s) => a + s.subtitles.reduce((b, sub) => b + sub.features.length, 0), 0
  )

  const nameMap = Object.fromEntries(
    (services || []).map(s => [s.value, s.title?.trim() || s.label])
  )

  return (
    <div className="rfp-features">

      {/* ── Page header ── */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="rfp-title mb-0">Project Overview</h4>
      </div>

      {/* ── Row 1: Timing + Cost ── */}
      <div className="row g-3 mb-4">

        {/* Timing Summary */}
        <div className="col-md-6">
          <div className="rfp-inner-card h-100">
            <div className="rfp-inner-header">
              <h6 className="rfp-overview-section-title">Timing Summary</h6>
              <span className="rfp-timing-pill ms-auto">{grandHrs} hrs total</span>
            </div>
            <div className="rfp-inner-body">

              {features.map(svc => {
                const svcName = nameMap[svc.serviceId] || svc.serviceId
                const svcTotal = svc.subtitles.reduce(
                  (acc, sub, fi) => acc + featureTotal(timing, svc.serviceId, fi, sub.features.length),
                  0
                )
                return (
                  <div key={svc.serviceId} className="rfp-overview-timing-row">
                    <span className="rfp-overview-timing-label">{svcName}</span>
                    <span className="rfp-overview-timing-value">{svcTotal} hrs</span>
                  </div>
                )
              })}

              <div className="rfp-overview-divider">
                <div className="rfp-overview-timing-row">
                  <span className="rfp-overview-timing-label">
                    Developer
                    <span className="rfp-hours-auto-tag ms-1">auto</span>
                  </span>
                  <span className="rfp-overview-timing-value">{devHours} hrs</span>
                </div>
                <div className="rfp-overview-timing-row">
                  <span className="rfp-overview-timing-label">
                    BA
                    <span className="rfp-hours-auto-tag ms-1">auto</span>
                  </span>
                  <span className="rfp-overview-timing-value">{baHours} hrs</span>
                </div>
                <div className="rfp-overview-timing-row">
                  <span className="rfp-overview-timing-label">
                    QA
                    <span className="rfp-hours-auto-tag ms-1">auto</span>
                  </span>
                  <span className="rfp-overview-timing-value">{qaHours} hrs</span>
                </div>
              </div>

              <div className="rfp-mini-total-bar">
                <span className="rfp-mini-total-label">Grand Total Hours</span>
                <span className="rfp-mini-total-value">{grandHrs} hrs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Summary */}
        <div className="col-md-6">
          <div className="rfp-inner-card h-100">
            <div className="rfp-inner-header">
              <h6 className="rfp-overview-section-title">Cost Summary</h6>
              <span className="rfp-timing-pill ms-auto">{currSymbol}</span>
            </div>
            <div className="rfp-inner-body">

              {/* Empty state */}
              {/* {!savedCosting ? (
                <p className="text-muted text-center py-3" style={{ fontSize: "13px" }}>
                  Costing not saved yet
                </p>
              ) : (
                <>
                  <div className="rfp-overview-cost-row">
                    <span className="rfp-overview-cost-label">
                      Technology
                      {savedCosting.technology && (
                        <span className="rfp-hours-auto-tag ms-1">
                          {savedCosting.technology}
                        </span>
                      )}
                    </span>
                    
                    <span className="rfp-overview-cost-formula" style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
  <span>{grandHrs} hrs × {currSymbol}{Number(savedCosting.final_rate_per_hr || 0).toFixed(2)}/hr</span>
  {savedCosting.overhead_per_hr > 0 && (
    <span style={{ fontSize: "10px", color: "#888" }}>
      base {currSymbol}{Number(savedCosting.base_rate_per_hr).toFixed(2)} + overhead {currSymbol}{Number(savedCosting.overhead_per_hr).toFixed(2)}
    </span>
  )}
</span>
                    <span className="rfp-overview-cost-value">
                      {fmt(savedCosting.base_cost, currSymbol)}
                    </span>
                  </div>

                  {savedCosting.commission?.pct > 0 && (
                    <div className="rfp-overview-cost-row">
                      <span className="rfp-overview-cost-label">
                        Commission ({savedCosting.commission.pct}%)
                      </span>
                      <span className="rfp-overview-cost-formula" />
                      <span className="rfp-overview-cost-value">
                        +{fmt(savedCosting.commission.amount, currSymbol)}
                      </span>
                    </div>
                  )}

                  {savedCosting.gocardless?.enabled && savedCosting.gocardless.pct > 0 && (
                    <div className="rfp-overview-cost-row">
                      <span className="rfp-overview-cost-label">
                        Go Cardless ({savedCosting.gocardless.pct}%)
                      </span>
                      <span className="rfp-overview-cost-formula" />
                      <span className="rfp-overview-cost-value">
                        +{fmt(savedCosting.gocardless.amount, currSymbol)}
                      </span>
                    </div>
                  )}

                  {savedCosting.vat?.pct > 0 && (
                    <div className="rfp-overview-cost-row">
                      <span className="rfp-overview-cost-label">
                        VAT ({savedCosting.vat.pct}%)
                      </span>
                      <span className="rfp-overview-cost-formula" />
                      <span className="rfp-overview-cost-value">
                        +{fmt(savedCosting.vat.amount, currSymbol)}
                      </span>
                    </div>
                  )}

                  <div className="rfp-mini-total-bar">
                    <span className="rfp-mini-total-label">Grand Total</span>
                    <span className="rfp-mini-total-value">
                      {fmt(savedCosting.grand_total, currSymbol)}
                    </span>
                  </div>
                </>
              )} */}

              {!savedCosting ? (
                <p className="text-muted text-center py-3" style={{ fontSize: "13px" }}>
                  Costing not saved yet
                </p>
              ) : (
                <>
                  {/* Developer row */}
                  <div className="rfp-overview-cost-row">
                    <span className="rfp-overview-cost-label">
                      Developer
                      {savedCosting.dev?.technology && (
                        <span className="rfp-hours-auto-tag ms-1">{savedCosting.dev.technology}</span>
                      )}
                    </span>
                    <span className="rfp-overview-cost-formula">
                      {savedCosting.dev?.hours || 0} hrs × {currSymbol}{Number(savedCosting.dev?.rate || 0).toFixed(2)}/hr
                    </span>
                    <span className="rfp-overview-cost-value">
                      {fmt(savedCosting.dev?.cost, currSymbol)}
                    </span>
                  </div>

                  {/* BA row */}
                  <div className="rfp-overview-cost-row">
                    <span className="rfp-overview-cost-label">
                      BA
                      {savedCosting.ba?.technology && (
                        <span className="rfp-hours-auto-tag ms-1">{savedCosting.ba.technology}</span>
                      )}
                    </span>
                    <span className="rfp-overview-cost-formula">
                      {savedCosting.ba?.hours || 0} hrs × {currSymbol}{Number(savedCosting.ba?.rate || 0).toFixed(2)}/hr
                    </span>
                    <span className="rfp-overview-cost-value">
                      {fmt(savedCosting.ba?.cost, currSymbol)}
                    </span>
                  </div>

                  {/* QA row */}
                  <div className="rfp-overview-cost-row">
                    <span className="rfp-overview-cost-label">
                      QA
                      {savedCosting.qa?.technology && (
                        <span className="rfp-hours-auto-tag ms-1">{savedCosting.qa.technology}</span>
                      )}
                    </span>
                    <span className="rfp-overview-cost-formula">
                      {savedCosting.qa?.hours || 0} hrs × {currSymbol}{Number(savedCosting.qa?.rate || 0).toFixed(2)}/hr
                    </span>
                    <span className="rfp-overview-cost-value">
                      {fmt(savedCosting.qa?.cost, currSymbol)}
                    </span>
                  </div>

                  {/* Overhead row */}
                  {savedCosting.overhead?.cost > 0 && (
                    <div className="rfp-overview-cost-row">
                      <span className="rfp-overview-cost-label">Overhead</span>
                      <span className="rfp-overview-cost-formula">
                        {savedCosting.overhead.hours} hrs × {currSymbol}{Number(savedCosting.overhead.per_hr).toFixed(2)}/hr
                      </span>
                      <span className="rfp-overview-cost-value">
                        {fmt(savedCosting.overhead.cost, currSymbol)}
                      </span>
                    </div>
                  )}

                  {/* Base Cost */}
                  {/* <div className="rfp-overview-cost-row rfp-overview-cost-row--subtotal">
                    <span className="rfp-overview-cost-label fw-semibold">Base Cost</span>
                    <span className="rfp-overview-cost-formula" />
                    <span className="rfp-overview-cost-value fw-semibold">
                      {fmt(savedCosting.base_cost, currSymbol)}
                    </span>
                  </div> */}

                  {/* Profit */}
                  {savedCosting.profit_pct > 0 && (
                    <div className="rfp-overview-cost-row">
                      <span className="rfp-overview-cost-label">Profit ({savedCosting.profit_pct}%)</span>
                      <span className="rfp-overview-cost-formula" />
                      <span className="rfp-overview-cost-value">+{fmt(savedCosting.profit_amount, currSymbol)}</span>
                    </div>
                  )}

                  {/* Commission */}
                  {savedCosting.commission?.pct > 0 && (
                    <div className="rfp-overview-cost-row">
                      <span className="rfp-overview-cost-label">Commission ({savedCosting.commission.pct}%)</span>
                      <span className="rfp-overview-cost-formula" />
                      <span className="rfp-overview-cost-value">+{fmt(savedCosting.commission.amount, currSymbol)}</span>
                    </div>
                  )}

                  {/* Sub Total */}
                  <div className="rfp-overview-cost-row rfp-overview-cost-row--subtotal">
                    <span className="rfp-overview-cost-label fw-semibold">Sub Total</span>
                    <span className="rfp-overview-cost-formula" />
                    <span className="rfp-overview-cost-value fw-semibold">
                      {fmt(
                        (Number(savedCosting.base_cost) || 0) +
                        (Number(savedCosting.profit_amount) || 0) +
                        (Number(savedCosting.commission?.amount) || 0),
                        currSymbol
                      )}
                    </span>
                  </div>

                  {/* GST */}
                  {savedCosting.gst?.pct > 0 && (
                    <div className="rfp-overview-cost-row">
                      <span className="rfp-overview-cost-label">GST ({savedCosting.gst.pct}%)</span>
                      <span className="rfp-overview-cost-formula" />
                      <span className="rfp-overview-cost-value">+{fmt(savedCosting.gst.amount, currSymbol)}</span>
                    </div>
                  )}

                  {/* Grand Total */}
                  <div className="rfp-mini-total-bar">
                    <span className="rfp-mini-total-label">Grand Total</span>
                    <span className="rfp-mini-total-value">
                      {fmt(savedCosting.grand_total, currSymbol)}
                    </span>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ── Row 2: Features read-only ── */}
      <div className="rfp-inner-card">
        <div className="rfp-inner-header">
          <h6 className="rfp-overview-section-title">Features</h6>
          <span className="rfp-count-pill ms-auto">
            {totalFeatures} feature{totalFeatures !== 1 ? "s" : ""} · {totalSubFeatures} sub-feature{totalSubFeatures !== 1 ? "s" : ""}
          </span>
        </div>

        {features.map(svc => {
          const svcName = nameMap[svc.serviceId] || svc.serviceId
          return (
            <div key={svc.serviceId}>
              <div className="rfp-service-header rfp-service-header--open">
                <h5 className="rfp-service-display-name mb-0">{svcName}</h5>
                <span className="rfp-count-pill">
                  {svc.subtitles.length} feature{svc.subtitles.length !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="rfp-feature-grid-wrapper">
                <div className="rfp-feature-grid">
                  {svc.subtitles.map((feature, fi) => {
                    const fHrs = featureTotal(timing, svc.serviceId, fi, feature.features.length)
                    return (
                      <div key={fi} className="rfp-feature-card-mini">
                        <div className="rfp-fcm-header">
                          <span className="rfp-fcm-sr">{fi + 1}</span>
                          <span className="rfp-fcm-title">
                            {feature.title || <em className="text-muted">Untitled</em>}
                          </span>
                          {fHrs > 0 && (
                            <span className="rfp-timing-pill" style={{ fontSize: "10px", padding: "1px 6px" }}>
                              {fHrs}h
                            </span>
                          )}
                        </div>

                        {feature.features.length > 0 && (
                          <div className="rfp-subs-list">
                            {feature.features.map((sf, si) => {
                              const sfHrs = ROLES.reduce(
                                (acc, role) => acc + getHrs(timing, timingKey(svc.serviceId, fi, si, role)),
                                0
                              )
                              return (
                                <div key={si} className="rfp-sub-row">
                                  <span className="rfp-sub-dot">•</span>
                                  <span className="rfp-sub-title">
                                    {sf.title || <em className="text-muted">Untitled</em>}
                                  </span>
                                  {sfHrs > 0 && (
                                    <span className="rfp-sf-hrs">{sfHrs}h</span>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}

                        {feature.features.length > 0 && (
                          <div className="rfp-fcm-footer">
                            <span className="rfp-sub-count">
                              {feature.features.length} sub-feature{feature.features.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Activity Trail ── */}
      {stepLogs && (
        <div className="rfp-activity-log mt-4">
          <div className="rfp-activity-log-title">Activity</div>
          <div className="d-flex gap-3 flex-wrap">
            {(() => {
              const cards = [
                {
                  label: "Created By",
                  sublabel: null,
                  entry: stepLogs.step2?.saved?.[0],
                  active: !!stepLogs.step2?.saved?.[0],
                },
                {
                  label: "Level 1",
                  sublabel: "(Timing)",
                  entry: stepLogs.step3?.submitted?.at(-1),
                  active: !!stepLogs.step3?.submitted?.at(-1),
                },
                {
                  label: "Level 2",
                  sublabel: "(Costing)",
                  entry: stepLogs.step4?.saved?.at(-1),
                  active:
                    !!stepLogs.step4?.saved?.at(-1) &&
                    !!stepLogs.step3?.approved?.at(-1),
                },
                {
                  label: "Final Approval",
                  sublabel: "(Overview)",
                  entry: stepLogs.step5?.approved?.at(-1),
                  active:
                    !!stepLogs.step5?.approved?.at(-1) &&
                    !!stepLogs.step4?.saved?.at(-1) &&
                    !!stepLogs.step3?.approved?.at(-1),
                },
              ]

              const visibleCards = []
              for (const card of cards) {
                if (!card.active) break
                visibleCards.push(card)
              }

              return visibleCards.map((card, i) => {
                const user = allUsers?.find(u => u.id == card.entry.by)
                return (
                  <div key={i} className="rfp-activity-pill">
                    <div className="rfp-activity-icon">
                      {user?.avatar
                        ? <img
                          src={`${configure.imgUrl}${user.avatar}`}
                          alt={user.name}
                          style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                        />
                        : <i className="fas fa-user" />}
                    </div>
                    <div>
                      <div className="rfp-activity-label">
                        {card.label}{" "}
                        {card.sublabel && (
                          <span style={{ fontWeight: 400, textTransform: "none" }}>
                            {card.sublabel}
                          </span>
                        )}
                      </div>
                      <div className="rfp-activity-user">
                        {user?.name || `User #${card.entry.by}`}
                      </div>
                      <div className="rfp-activity-time">
                        {new Date(card.entry.at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        </div>
      )}

    </div>
  )
}

export default StepOverview
